import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { format } from 'date-fns';
import { CalendarIcon, FileText, MessageSquare } from 'lucide-react';
import RichTextEditor from './RichTextEditor';

const AddInteractionModal = ({ open, onOpenChange, onSubmit, editInteraction }) => {
  const [activeTab, setActiveTab] = useState('summary');
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  const [summaryData, setSummaryData] = useState({
    date: '',
    summary_text: ''
  });
  
  const [detailedData, setDetailedData] = useState({
    interaction_type: 'meeting',
    date: '',
    city: '',
    location: '',
    setup_method: '',
    connected_by: '',
    connected_by_company: '',
    key_points: '',
    questions_asked: '',
    quotes: '',
    future_course_of_action: ''
  });

  // Pre-fill form when editing
  useEffect(() => {
    if (editInteraction) {
      if (editInteraction.is_summary) {
        setActiveTab('summary');
        setSummaryData({
          date: editInteraction.date || '',
          summary_text: editInteraction.summary_text || ''
        });
      } else {
        setActiveTab('detailed');
        setDetailedData({
          interaction_type: editInteraction.interaction_type || 'meeting',
          date: editInteraction.date || '',
          city: editInteraction.city || '',
          location: editInteraction.location || '',
          setup_method: editInteraction.setup_method || '',
          connected_by: editInteraction.connected_by || '',
          connected_by_company: editInteraction.connected_by_company || '',
          key_points: editInteraction.key_points || '',
          questions_asked: editInteraction.questions_asked || '',
          quotes: editInteraction.quotes || '',
          future_course_of_action: editInteraction.future_course_of_action || ''
        });
      }
    } else {
      resetForm();
    }
  }, [editInteraction]);

  const handleSummarySubmit = () => {
    onSubmit({
      ...summaryData,
      is_summary: true,
      interaction_type: 'note'
    });
    resetForm();
  };

  const handleDetailedSubmit = () => {
    onSubmit({
      ...detailedData,
      is_summary: false
    });
    resetForm();
  };

  const resetForm = () => {
    setSummaryData({ date: '', summary_text: '' });
    setDetailedData({
      interaction_type: 'meeting',
      date: '',
      city: '',
      location: '',
      setup_method: '',
      connected_by: '',
      connected_by_company: '',
      key_points: '',
      questions_asked: '',
      quotes: '',
      future_course_of_action: ''
    });
    setActiveTab('summary');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-heading text-[#0F172A]">
            {editInteraction ? 'Edit Interaction Notes' : 'Add Interaction Notes'}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="summary" className="gap-2" data-testid="tab-summary">
              <MessageSquare className="w-4 h-4" />
              Quick Summary
            </TabsTrigger>
            <TabsTrigger value="detailed" className="gap-2" data-testid="tab-detailed">
              <FileText className="w-4 h-4" />
              Detailed Notes
            </TabsTrigger>
          </TabsList>

          {/* Quick Summary Tab */}
          <TabsContent value="summary" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    data-testid="summary-date-button"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {summaryData.date 
                      ? format(new Date(summaryData.date), 'PPP')
                      : 'Select date'
                    }
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-[100]" align="start">
                  <Calendar
                    mode="single"
                    selected={summaryData.date ? new Date(summaryData.date) : undefined}
                    onSelect={(date) => {
                      setSummaryData(prev => ({
                        ...prev,
                        date: date ? `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}` : ''
                      }));
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Summary</Label>
              <Textarea
                value={summaryData.summary_text}
                onChange={(e) => setSummaryData(prev => ({ ...prev, summary_text: e.target.value }))}
                placeholder="e.g., Ashwin called. He is leaving Goldman Sachs."
                rows={4}
                data-testid="summary-text-input"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="cancel-interaction-button"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSummarySubmit}
                disabled={!summaryData.date || !summaryData.summary_text}
                className="bg-[#0F172A] hover:bg-[#1E293B] text-white"
                data-testid="submit-summary-button"
              >
                {editInteraction ? 'Update Summary' : 'Save Summary'}
              </Button>
            </div>
          </TabsContent>

          {/* Detailed Notes Tab */}
          <TabsContent value="detailed" className="space-y-4 mt-4">
            <div className="space-y-3">
              <Label className="text-slate-700 font-medium">Meeting or Call</Label>
              <RadioGroup
                value={detailedData.interaction_type}
                onValueChange={(value) => setDetailedData(prev => ({ ...prev, interaction_type: value }))}
                className="flex gap-6"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="meeting" id="modal-meeting" data-testid="modal-meeting" />
                  <Label htmlFor="modal-meeting" className="cursor-pointer">Meeting</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="call" id="modal-call" data-testid="modal-call" />
                  <Label htmlFor="modal-call" className="cursor-pointer">Call</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Date</Label>
                <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="detailed-date-button"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {detailedData.date 
                        ? format(new Date(detailedData.date), 'PPP')
                        : 'Select date'
                      }
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[100]" align="start">
                    <Calendar
                      mode="single"
                      selected={detailedData.date ? new Date(detailedData.date) : undefined}
                      onSelect={(date) => {
                        setDetailedData(prev => ({
                          ...prev,
                          date: date ? `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}` : ''
                        }));
                        setShowDatePicker(false);
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {detailedData.interaction_type === 'meeting' && (
                <>
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">City of Meeting</Label>
                    <Input
                      value={detailedData.city}
                      onChange={(e) => setDetailedData(prev => ({ ...prev, city: e.target.value }))}
                      placeholder="e.g., Hong Kong"
                      data-testid="modal-city-input"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label className="text-slate-700 font-medium">Location</Label>
                    <Input
                      value={detailedData.location}
                      onChange={(e) => setDetailedData(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="e.g., Mandarin Oriental"
                      data-testid="modal-location-input"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="space-y-3">
              <Label className="text-slate-700 font-medium">How was this set up?</Label>
              <RadioGroup
                value={detailedData.setup_method}
                onValueChange={(value) => setDetailedData(prev => ({ ...prev, setup_method: value }))}
                className="space-y-2"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="reached_out_by_them" id="modal-them" data-testid="modal-setup-them" />
                  <Label htmlFor="modal-them" className="cursor-pointer">He/She reached out</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="reached_out_by_me" id="modal-me" data-testid="modal-setup-me" />
                  <Label htmlFor="modal-me" className="cursor-pointer">I reached out</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="connected_by" id="modal-connected" data-testid="modal-setup-connected" />
                  <Label htmlFor="modal-connected" className="cursor-pointer">Connected by someone</Label>
                </div>
              </RadioGroup>
              {detailedData.setup_method === 'connected_by' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                  <Input
                    value={detailedData.connected_by}
                    onChange={(e) => setDetailedData(prev => ({ ...prev, connected_by: e.target.value }))}
                    placeholder="Connected by whom?"
                    data-testid="modal-connected-by-input"
                  />
                  <Input
                    value={detailedData.connected_by_company}
                    onChange={(e) => setDetailedData(prev => ({ ...prev, connected_by_company: e.target.value }))}
                    placeholder="Their company"
                    data-testid="modal-connected-by-company-input"
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Key Points of Meeting</Label>
              <RichTextEditor
                value={detailedData.key_points}
                onChange={(value) => setDetailedData(prev => ({ ...prev, key_points: value }))}
                placeholder="Add detailed notes about the meeting..."
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Questions Asked <span className="text-slate-400 font-normal">(optional)</span></Label>
              <RichTextEditor
                value={detailedData.questions_asked}
                onChange={(value) => setDetailedData(prev => ({ ...prev, questions_asked: value }))}
                placeholder="Questions asked during the meeting..."
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Quotes <span className="text-slate-400 font-normal">(if any)</span></Label>
              <RichTextEditor
                value={detailedData.quotes}
                onChange={(value) => setDetailedData(prev => ({ ...prev, quotes: value }))}
                placeholder="Notable quotes from the meeting..."
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Future Course of Action</Label>
              <RichTextEditor
                value={detailedData.future_course_of_action}
                onChange={(value) => setDetailedData(prev => ({ ...prev, future_course_of_action: value }))}
                placeholder="Next steps, follow-ups, action items..."
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="cancel-detailed-button"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDetailedSubmit}
                disabled={!detailedData.key_points && !detailedData.date}
                className="bg-[#0F172A] hover:bg-[#1E293B] text-white"
                data-testid="submit-detailed-button"
              >
                {editInteraction ? 'Update Notes' : 'Save Notes'}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default AddInteractionModal;
